import { useContext } from 'react';
import type { ReactNode } from 'react';
import { FolderOpenIcon, HomeIcon } from 'lucide-react';
import { Link, useLocation } from 'react-router';

import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';
import { AppBreadcrumbCtx } from '@/hooks/app-breadcrumb-store';
import { usePageChrome } from '@/hooks/page-chrome-store';
import { ProjectCtx, type ProjectContext } from '@/hooks/project-context';
import { TenantCtx, type TenantContext } from '@/hooks/tenant-context';
import { projectPath, tenantPath } from '@/lib/routes';

type BreadcrumbIcon = 'home' | 'projects';

interface BreadcrumbSegment {
  readonly href: string | undefined;
  readonly icon: BreadcrumbIcon | undefined;
  readonly label: string;
  /** Entity names yield space before short navigation labels. */
  readonly shrink?: boolean;
}

const globalRouteLabels = {
  admin: 'Platform Admin',
  'control-plane': 'Control plane',
  settings: 'Settings',
};

const routeLabels = {
  annotate: 'Annotate',
  compare: 'Compare',
  documents: 'Documents',
  edit: 'Edit Template',
  evaluations: 'Evaluations',
  'ground-truth': 'Ground Truth',
  members: 'Members',
  settings: 'Settings',
  templates: 'Templates',
  upload: 'Upload documents',
  'upload-documents': 'Upload documents',
};

const tenantLevelLabels = {
  documents: 'All Documents',
};

const uuidPattern =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const breadcrumbLabelClass =
  'inline-block min-w-0 max-w-[16ch] truncate align-bottom sm:max-w-[20ch]';

function formatFallbackLabel(segment: string): string {
  const label = segment.replace(/-/g, ' ');
  return label.charAt(0).toUpperCase() + label.slice(1);
}

function formatCompactIdentifier(segment: string): string {
  if (uuidPattern.test(segment)) {
    return segment.slice(0, 8).toUpperCase();
  }

  const compact = segment.replace(/[^a-z0-9]/gi, '');
  if (compact.length >= 8) {
    return compact.slice(0, 8).toUpperCase();
  }

  if (segment.length <= 18) {
    return segment;
  }

  return `${segment.slice(0, 8)}…${segment.slice(-4)}`;
}

function formatIdentifierLabel(segment: string): string {
  return `#${formatCompactIdentifier(segment)}`;
}

function getProjectName(project: ProjectContext['project']): string | undefined {
  if ('name' in project && typeof project.name === 'string') {
    const name = project.name.trim();
    if (name.length > 0) {
      return name;
    }
  }

  if ('display_name' in project && typeof project.display_name === 'string') {
    const displayName = project.display_name.trim();
    if (displayName.length > 0) {
      return displayName;
    }
  }

  return undefined;
}

function getProjectLabel(project: ProjectContext | null, projectId: string): string {
  const projectName = project ? getProjectName(project.project) : undefined;
  return projectName ?? formatCompactIdentifier(projectId);
}

function lookupLabel(labels: Readonly<Record<string, string>>, key: string): string | undefined {
  return Object.hasOwn(labels, key) ? labels[key] : undefined;
}

function appendUnknownSegments(
  breadcrumbs: BreadcrumbSegment[],
  basePath: string,
  segments: readonly string[],
): void {
  segments.forEach((segment, index) => {
    const isLast = index === segments.length - 1;
    const href = isLast ? undefined : `${basePath}/${segments.slice(0, index + 1).join('/')}`;
    breadcrumbs.push({
      href,
      icon: undefined,
      label: lookupLabel(routeLabels, segment) ?? formatFallbackLabel(segment),
    });
  });
}

/** Template navigation shares names and actions across project, library and TSA scopes. */
function appendTemplateBreadcrumbs(
  breadcrumbs: BreadcrumbSegment[],
  templatesRoot: string,
  list: { readonly href: string; readonly label: string },
  segments: readonly string[],
  templateLabel: string | null,
): void {
  const templateId = segments[0];
  breadcrumbs.push({
    href: templateId === undefined ? undefined : list.href,
    icon: undefined,
    label: list.label,
  });

  if (templateId === undefined) return;

  if (templateId === 'new') {
    breadcrumbs.push({ href: undefined, icon: undefined, label: 'New Template' });
    return;
  }

  const templatePath = `${templatesRoot}/${templateId}`;
  breadcrumbs.push({
    href: segments.length > 1 ? templatePath : undefined,
    icon: undefined,
    label: templateLabel ?? `Template ${formatIdentifierLabel(templateId)}`,
    shrink: true,
  });
  appendUnknownSegments(breadcrumbs, templatePath, segments.slice(1));
}

function buildProjectBreadcrumbs(
  tenantSlug: string,
  projectId: string,
  trailingSegments: readonly string[],
  project: ProjectContext | null,
  templateLabel: string | null,
): BreadcrumbSegment[] {
  const projectRoot = projectPath(tenantSlug, projectId);
  const projectsRoot = tenantPath(tenantSlug, '/projects');
  const breadcrumbs: BreadcrumbSegment[] = [
    {
      href: projectsRoot,
      icon: 'projects',
      label: 'Projects',
    },
    {
      href: trailingSegments.length === 0 ? undefined : projectRoot,
      icon: undefined,
      label: getProjectLabel(project, projectId),
      shrink: true,
    },
  ];

  const section = trailingSegments[0];
  if (section === undefined) {
    return breadcrumbs;
  }

  if (section === 'templates') {
    const templatesRoot = `${projectRoot}/templates`;
    appendTemplateBreadcrumbs(
      breadcrumbs, templatesRoot, { href: templatesRoot, label: 'Templates' },
      trailingSegments.slice(1), templateLabel,
    );
    return breadcrumbs;
  }

  if (section === 'runs' || section === 'analyses') {
    breadcrumbs.push({
      href: undefined,
      icon: undefined,
      label: 'Redirecting…',
    });
    return breadcrumbs;
  }

  if (
    section === 'documents' ||
    section === 'upload' ||
    section === 'upload-documents'
  ) {
    breadcrumbs.push({
      href: undefined,
      icon: undefined,
      label: section === 'documents' ? 'Documents' : 'Upload documents',
    });
    return breadcrumbs;
  }

  if (section === 'evaluations') {
    const evaluationSegment = trailingSegments[1];
    breadcrumbs.push({
      href: evaluationSegment === undefined ? undefined : `${projectRoot}/evaluations`,
      icon: undefined,
      label: 'Evaluations',
    });

    if (evaluationSegment === undefined) {
      return breadcrumbs;
    }

    if (evaluationSegment === 'compare') {
      breadcrumbs.push({
        href: undefined,
        icon: undefined,
        label: 'Compare',
      });
      return breadcrumbs;
    }

    const evaluationPath = `${projectRoot}/evaluations/${evaluationSegment}`;
    breadcrumbs.push({
      href: undefined,
      icon: undefined,
      label: `Evaluation ${formatIdentifierLabel(evaluationSegment)}`,
    });

    if (trailingSegments.length > 2) {
      appendUnknownSegments(breadcrumbs, evaluationPath, trailingSegments.slice(2));
    }

    return breadcrumbs;
  }

  if (section === 'ground-truth') {
    const actionSegment = trailingSegments[1];
    breadcrumbs.push({
      href: actionSegment === undefined ? undefined : `${projectRoot}/ground-truth`,
      icon: undefined,
      label: 'Ground Truth',
    });

    if (actionSegment === undefined) {
      return breadcrumbs;
    }

    if (actionSegment === 'annotate') {
      const documentId = trailingSegments[2];
      const annotatePath = `${projectRoot}/ground-truth/annotate`;
      breadcrumbs.push({
        href: documentId === undefined ? undefined : annotatePath,
        icon: undefined,
        label: 'Annotate',
      });

      if (documentId === undefined) {
        return breadcrumbs;
      }

      breadcrumbs.push({
        href: undefined,
        icon: undefined,
        label: `Document ${formatIdentifierLabel(documentId)}`,
      });

      if (trailingSegments.length > 3) {
        appendUnknownSegments(breadcrumbs, `${annotatePath}/${documentId}`, trailingSegments.slice(3));
      }

      return breadcrumbs;
    }

    appendUnknownSegments(breadcrumbs, `${projectRoot}/ground-truth`, trailingSegments.slice(1));
    return breadcrumbs;
  }

  if (section === 'tsa-workspace') {
    // Inside the TSA workspace the templates list lives on the workspace page
    // itself, so the Template library crumb should link back to the workspace
    // rather than to a non-existent `/tsa-workspace/templates` route.
    const workspaceRoot = `${projectRoot}/tsa-workspace`;
    const sub = trailingSegments[1];
    breadcrumbs.push({
      href: sub === undefined ? undefined : workspaceRoot,
      icon: undefined,
      label: 'TSA Workspace',
    });

    if (sub === undefined) {
      return breadcrumbs;
    }

    if (sub === 'templates') {
      appendTemplateBreadcrumbs(
        breadcrumbs, `${workspaceRoot}/templates`, { href: workspaceRoot, label: 'Template library' },
        trailingSegments.slice(2), templateLabel,
      );
      return breadcrumbs;
    }

    appendUnknownSegments(breadcrumbs, workspaceRoot, trailingSegments.slice(1));
    return breadcrumbs;
  }

  appendUnknownSegments(breadcrumbs, projectRoot, trailingSegments);
  return breadcrumbs;
}

function buildTenantBreadcrumbs(
  tenantSlug: string,
  trailingSegments: readonly string[],
  project: ProjectContext | null,
  templateLabel: string | null,
): BreadcrumbSegment[] {
  if (trailingSegments[0] === 'projects' && trailingSegments[1] !== undefined) {
    return buildProjectBreadcrumbs(
      tenantSlug,
      trailingSegments[1],
      trailingSegments.slice(2),
      project,
      templateLabel,
    );
  }

  const breadcrumbs: BreadcrumbSegment[] = [
    {
      href: trailingSegments.length === 0 ? undefined : tenantPath(tenantSlug),
      icon: trailingSegments.length === 0 ? 'home' : 'projects',
      label: trailingSegments.length === 0 ? 'Dashboard' : 'Dashboard',
    },
  ];

  const section = trailingSegments[0];
  if (section === undefined) {
    return breadcrumbs;
  }

  if (section === 'runs' || section === 'analyses') {
    breadcrumbs.push({
      href: undefined,
      icon: undefined,
      label: 'Redirecting…',
    });
    return breadcrumbs;
  }

  if (section === 'projects') {
    breadcrumbs.push({
      href: trailingSegments[1] === undefined ? undefined : tenantPath(tenantSlug, '/projects'),
      icon: 'projects',
      label: 'Projects',
    });

    if (trailingSegments[1] === undefined) {
      return breadcrumbs;
    }
  }

  if (section === 'documents') {
    breadcrumbs.push({
      href: undefined,
      icon: undefined,
      label: tenantLevelLabels.documents,
    });
    return breadcrumbs;
  }

  if (section === 'templates') {
    const templatesRoot = tenantPath(tenantSlug, 'templates');
    appendTemplateBreadcrumbs(
      breadcrumbs, templatesRoot, { href: templatesRoot, label: 'Template library' },
      trailingSegments.slice(1), templateLabel,
    );
    return breadcrumbs;
  }

  if (section === 'members' || section === 'settings') {
    breadcrumbs.push({
      href: undefined,
      icon: undefined,
      label: section === 'members' ? 'Members' : 'Settings',
    });
    return breadcrumbs;
  }

  appendUnknownSegments(breadcrumbs, tenantPath(tenantSlug), trailingSegments);
  return breadcrumbs;
}

function buildGlobalBreadcrumbs(pathSegments: readonly string[]): BreadcrumbSegment[] {
  const rootSegment = pathSegments[0];
  if (rootSegment === undefined) {
    return [
      {
        href: undefined,
        icon: 'home',
        label: 'Home',
      },
    ];
  }

  const breadcrumbs: BreadcrumbSegment[] = [
    {
      href: pathSegments.length === 1 ? undefined : `/${rootSegment}`,
      icon: 'home',
      label: lookupLabel(globalRouteLabels, rootSegment) ?? formatFallbackLabel(rootSegment),
    },
  ];

  if (pathSegments.length > 1) {
    appendUnknownSegments(breadcrumbs, `/${rootSegment}`, pathSegments.slice(1));
  }

  return breadcrumbs;
}

function buildBreadcrumbs(
  pathname: string,
  tenant: TenantContext | null,
  project: ProjectContext | null,
  templateLabel: string | null,
): BreadcrumbSegment[] {
  const pathSegments = pathname.split('/').filter(Boolean);
  const rootSegment = pathSegments[0];

  if (rootSegment === undefined || lookupLabel(globalRouteLabels, rootSegment) !== undefined) {
    return buildGlobalBreadcrumbs(pathSegments);
  }

  const tenantSlug = tenant?.slug === rootSegment ? tenant.slug : rootSegment;
  return buildTenantBreadcrumbs(tenantSlug, pathSegments.slice(1), project, templateLabel);
}

function renderSegmentLabel(segment: BreadcrumbSegment): ReactNode {
  if (segment.icon === undefined) {
    return segment.label;
  }

  const Icon = segment.icon === 'home' ? HomeIcon : FolderOpenIcon;
  return (
    <span className="inline-flex items-center gap-1.5">
      <Icon aria-hidden="true" className="size-3.5 shrink-0" data-icon />
      <span className="truncate">{segment.label}</span>
    </span>
  );
}

export function AppBreadcrumb() {
  const location = useLocation();
  const breadcrumbContext = useContext(AppBreadcrumbCtx);
  const tenant = useContext(TenantCtx);
  const project = useContext(ProjectCtx);
  const breadcrumbTailInUse = usePageChrome()?.breadcrumbTailInUse ?? false;
  const builtSegments = buildBreadcrumbs(
    location.pathname,
    tenant,
    project,
    breadcrumbContext?.templateLabel ?? null,
  );
  // A breadcrumb tail refines the current page (a result within Documents), so
  // the page's own crumb becomes the way back to its base view.
  const segments = breadcrumbTailInUse
    ? builtSegments.map((segment, index) =>
        index === builtSegments.length - 1 && segment.href === undefined
          ? { ...segment, href: location.pathname }
          : segment,
      )
    : builtSegments;

  return (
    <Breadcrumb>
      <BreadcrumbList>
        {segments.map((segment, index) => (
          <BreadcrumbItem
            key={`${segment.href ?? 'current'}-${segment.label}-${index}`}
            className={segment.shrink ? 'min-w-0' : 'shrink-0'}
          >
            {index > 0 ? <BreadcrumbSeparator className="shrink-0" /> : null}
            {segment.href ? (
              <BreadcrumbLink asChild className={breadcrumbLabelClass}>
                <Link to={segment.href} title={segment.label}>
                  {renderSegmentLabel(segment)}
                </Link>
              </BreadcrumbLink>
            ) : (
              <BreadcrumbPage className={breadcrumbLabelClass} title={segment.label}>
                {renderSegmentLabel(segment)}
              </BreadcrumbPage>
            )}
          </BreadcrumbItem>
        ))}
      </BreadcrumbList>
    </Breadcrumb>
  );
}
